'use client'

import { Sparkles } from 'lucide-react'
import { translations } from '@/lib/translations'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Autoplay } from 'swiper/modules'

// Import Swiper styles
import 'swiper/css'

// Image imports
import { useEffect, useState } from 'react'
import { fetchList, mediaUrl } from '@/lib/api'

export default function PartnersSection({ language }) {
  const t = translations[language]
  const [partners, setPartners] = useState([])

  useEffect(() => {
    fetchList('/partners')
      .then((rows) => setPartners(rows.map((row) => ({
        id: row.id,
        name: row.name,
        logo: mediaUrl(row.logo),
      }))))
      .catch(() => setPartners([]))
  }, [])

  return (
    <section id="partners" className="px-4">
      <div className="max-w-7xl mx-auto py-2">
        <div className="text-center mb-4 md:mb-4">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/5 border border-primary/10 mb-6 backdrop-blur-sm">
            <Sparkles className="w-4 h-4 text-primary" />
            <span className="text-sm font-medium text-primary/80 uppercase tracking-wide">
              Trusted Partners
            </span>
          </div>

          {/* Title */}
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
            <span className="bg-gradient-to-r from-primary via-primary/80 to-secondary bg-clip-text text-transparent">
              {t.partners.title}
            </span>
          </h2>

          {/* Animated Underline */}
          <div className="relative flex justify-center mb-6">
            <div className="w-24 h-1 bg-gradient-to-r from-primary/40 via-primary to-primary/40 rounded-full" />
            <div className="absolute w-12 h-1 bg-primary rounded-full animate-pulse" />
          </div>
        </div>

        <Swiper
          modules={[Autoplay]}
          spaceBetween={24}
          slidesPerView={3}
          loop={true}
          autoplay={{
            delay: 3000,
            disableOnInteraction: false,
            pauseOnMouseEnter: true,
          }}
          breakpoints={{
            320: { slidesPerView: 2, spaceBetween: 16 },
            480: { slidesPerView: 3, spaceBetween: 20 },
            640: { slidesPerView: 4, spaceBetween: 24 },
            768: { slidesPerView: 5, spaceBetween: 24 },
            1024: { slidesPerView: 6, spaceBetween: 24 },
            1280: { slidesPerView: 7, spaceBetween: 24 },
          }}
          className="py-6"
        >
          {partners.map((partner) => (
            <SwiperSlide key={partner.id}>
              <div className="group/partner aspect-square rounded-2xl bg-gradient-to-br from-card to-card/80 border-2 border-border/50 hover:border-primary hover:shadow-2xl hover:-translate-y-2 transition-all duration-500 flex flex-col items-center justify-center p-4 cursor-pointer relative overflow-hidden">
                {/* Glow Effect */}
                <div className="absolute inset-0 bg-gradient-to-br from-primary/0 via-primary/0 to-primary/5 opacity-0 group-hover/partner:opacity-100 transition-opacity duration-500" />
                
                {/* Logo */}
                <img
                  src={partner.logo.src || partner.logo}
                  alt={partner.name}
                  className="w-full h-full object-contain transition-all duration-500 group-hover/partner:scale-105"
                  loading="lazy"
                />
                
                {/* Hover Tooltip */}
                <div className="absolute bottom-0 left-0 right-0 bg-primary/90 backdrop-blur-sm text-white text-xs text-center py-1.5 transform translate-y-full group-hover/partner:translate-y-0 transition-transform duration-300">
                  <span className="font-medium">{partner.name}</span>
                </div>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

      <style jsx>{`
        @keyframes pulse {
          0%, 100% {
            opacity: 0.4;
          }
          50% {
            opacity: 1;
          }
        }
        
        .animate-pulse {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
      `}</style>
    </section>
  )
}